import canvasSize from 'canvas-size';
import { StylesForSVGView } from './SVGStyles';
import type { Rect } from '@view-model/models/common/basic';

type DrawSize = { width: number; height: number };

const ZoomScale = 0.6;

export class SVGToImage {
  /**
   * 実行中のブラウザがサポートする最大サイズから実際にCanvasに描画するサイズを算出する.
   * （なるべく高画質の画像にするため）
   *
   * @param drawSize 描画サイズ（補正前）
   * @param test ブラウザサポートしているサイズかをチェックする（OKの場合、補正しない）
   * @returns （実際の）描画サイズ
   */
  private static async adjustDrawSize(drawSize: DrawSize, test: boolean): Promise<DrawSize> {
    const { width, height } = await canvasSize.maxArea({ usePromise: true });
    if (test && drawSize.width <= width && drawSize.height <= height) {
      // 補正しない
      return drawSize;
    }

    const scaleW = width / drawSize.width;
    const scaleH = height / drawSize.height;

    const scale = Math.min(scaleW, scaleH);

    const adjustedDrawSize: DrawSize = {
      width: drawSize.width * scale,
      height: drawSize.height * scale,
    };

    return adjustedDrawSize;
  }

  /**
   * URLで参照される画像を読み込んで、そのデータをbase64形式にする.
   *
   * @param imageUrl 画像URL
   * @returns base64形式の画像データ文字列
   */
  private static async getBase64ImageFromUrl(imageUrl: string): Promise<string | null> {
    let blob: Blob | null = null;
    await fetch(imageUrl)
      .then(async (response) => {
        if (response.redirected) {
          // 画像URLがリダイレクトを経由する場合
          const redirectRes = await fetch(response.url);
          blob = await redirectRes.blob();
        } else {
          blob = await response.blob();
        }
      })
      .catch((e: unknown) => {
        console.error(e);
      });

    return new Promise<unknown>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
      if (blob === null) {
        reject('blob is null.');
        return;
      }
      reader.readAsDataURL(blob);
    })
      .then((base64Image: unknown) => {
        return base64Image as string;
      })
      .catch((e: unknown) => {
        console.error(e);
        return null;
      });
  }

  /**
   * 出力対象のsvg全体をbase64化する.
   *
   * @param svg SVG要素
   * @returns base64形式の画像データ文字列
   */
  private static async createBase64SVG(svg: SVGSVGElement) {
    // foreignObjectでラップしたstyleタグを作成
    const styleElem = document.createElement('style');
    styleElem.innerHTML = StylesForSVGView;

    const foreignObject = document.createElement('foreignObject');
    foreignObject.appendChild(styleElem);

    // 表示しているSVGに直接変更を加えられないので、cloneしたものにstyleタグを追加
    const cloned = svg.cloneNode(true) as SVGSVGElement;

    const images = cloned.querySelectorAll('img[src]:not([src=""])') as NodeListOf<HTMLImageElement>;

    await Promise.all(
      Array.from(images).map(async (img) => {
        if (!img.getAttribute('draggable')) {
          // 同じurlが設定されているimgタグが２つ存在するが、描画対象になっている方（draggable属性がない方）
          // にのみ画像データを設定する
          const base64Image = await this.getBase64ImageFromUrl(img.src);
          if (base64Image) {
            img.src = base64Image;
          }
        }
      })
    );

    // styleは先頭に挿入（最後に挿入すると適用順序の影響で見た目が変わってしまう）
    cloned.prepend(foreignObject);

    const svgData = new XMLSerializer().serializeToString(cloned);
    cloned.remove();

    return btoa(unescape(encodeURIComponent(svgData)));
  }

  /**
   * base64形式の画像データをcanvasに書き込んで、PNG画像としてダウンロードする.
   *
   * @param canvas キャンバス
   * @param base64SVGDataString base64形式の画像データ
   * @param drawSize canvasへの描画サイズ
   * @param fileName 出力画像ファイル名
   * @returns 成功時：true
   */
  private static loadAndDownloadImage(
    canvas: HTMLCanvasElement,
    base64SVGDataString: string,
    drawSize: DrawSize,
    fileName: string
  ): Promise<boolean> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = base64SVGDataString;
    })
      .then((img: HTMLImageElement) => {
        const context = canvas.getContext('2d');
        context?.drawImage(img, 0, 0, drawSize.width, drawSize.height);
        const pngDataUrl = canvas.toDataURL('image/png');

        const downloadLink = document.createElement('a');
        downloadLink.href = pngDataUrl;
        downloadLink.download = fileName;
        downloadLink.click();

        return true;
      })
      .catch((e: unknown) => {
        console.error(e);
        return false;
      });
  }

  /**
   * 指定したViewのG要素からSVG要素を作成し、PNG画像として出力する.
   *
   * @param gView g要素（View本体）
   * @param gViewTitle g要素（Viewタイトル部）
   * @param viewRect View矩形情報
   * @param fileName 出力画像ファイル名
   * @returns 成功時：true
   */
  static async exportFromViewParts(
    gView: SVGGElement,
    gViewTitle: SVGGElement,
    viewRect: Rect,
    fileName: string
  ): Promise<boolean> {
    const x = 0;
    const y = 0;

    // g要素（Viewタイトル部）
    const clonedViewTitle = gViewTitle.cloneNode(true) as SVGGElement;
    const draggableGElement = clonedViewTitle.children.item(0) as SVGGElement;
    const titleRect = draggableGElement.children.item(0) as SVGRectElement;
    const titleHeight = -titleRect.y.baseVal.value;
    draggableGElement.transform.baseVal.getItem(0).setTranslate(x, y + titleHeight);

    // ViewPopupMenuを出力対象から除外
    clonedViewTitle.children.item(1)?.remove();

    // g要素（View本体）
    const width = viewRect.width;
    const heightView = viewRect.height;
    const height = heightView + titleHeight;

    const clonedView = gView.cloneNode(true) as SVGGElement;
    clonedView.transform.baseVal.getItem(0).setTranslate(x, y + titleHeight);

    // SVG要素を作成してg要素を配下に追加する
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('width', `${width}`);
    svg.setAttribute('height', `${height}`);
    svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);

    svg.appendChild(clonedView);
    svg.appendChild(clonedViewTitle);

    // サイズ補正
    const drawSize = await this.adjustDrawSize({ width: width * ZoomScale, height: height * ZoomScale }, true);

    return this.exportInternal(svg, fileName, drawSize);
  }

  /**
   * 指定したSVG要素をPNG画像として出力する.
   *
   * @param svg SVG要素
   * @param fileName 出力画像ファイル名
   * @returns 成功時：true
   */
  static async export(svg: SVGSVGElement, fileName: string): Promise<boolean> {
    const drawSize = await this.adjustDrawSize(
      { width: svg.width.baseVal.value, height: svg.height.baseVal.value },
      false
    );
    return this.exportInternal(svg, fileName, drawSize);
  }

  private static async exportInternal(svg: SVGSVGElement, fileName: string, drawSize: DrawSize): Promise<boolean> {
    const base64SVG = await this.createBase64SVG(svg);

    const canvas = document.createElement('canvas');
    canvas.width = drawSize.width;
    canvas.height = drawSize.height;

    const base64SVGDataString = 'data:image/svg+xml;charset=utf-8;base64,' + base64SVG;
    const result = await this.loadAndDownloadImage(canvas, base64SVGDataString, drawSize, fileName);

    canvas.height = 0;
    canvas.width = 0;
    canvas.remove();

    return result;
  }
}
